import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonInput,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonToggle,
} from '@ionic/react';
import { chevronDownOutline, createOutline } from 'ionicons/icons';
import {
  PageHeaderBarProps,
  PageHeaderFilterControl,
  PageHeaderIconAction,
} from '@/types/model/pageHeaderBar';
import ActionIconButton from './ActionIconButton';
import { buildFilterSummary } from '@/services/util/pageHeaderBar/filterSummary';
import { createDebouncedDispatcher } from '@/services/util/pageHeaderBar/debounce';

const DEFAULT_SECTION_ORDER: Array<'filters' | 'search' | 'actions'> = ['filters', 'search', 'actions'];

const sectionContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  background: 'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)',
//   border: '1px solid #dce4ef',
//   borderRadius: '999px',
  padding: '0 14px',
  height: '56px',
  minWidth: 0,
  boxSizing: 'border-box',
};

const compactButtonStyle: React.CSSProperties = {
  paddingLeft: '6px',
  paddingRight: '6px',
  minHeight: '34px',
  height: '34px',
  borderRadius: '999px',
  textTransform: 'none',
};

const PageHeaderBar = <TValue extends string | number = string | number>({
  filters,
  search,
  actions,
  sectionOrder = DEFAULT_SECTION_ORDER,
  isMobileOverride,
  collapsible = true,
  initiallyCollapsed = false,
  collapseLabel = 'Page Controls',
}: PageHeaderBarProps<TValue>) => {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth <= 768);
  const [isEditingFilters, setIsEditingFilters] = useState(false);
  const [draftControls, setDraftControls] = useState<PageHeaderFilterControl<TValue>[]>(filters?.controls ?? []);
  const [searchInput, setSearchInput] = useState(search?.value ?? '');
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [localRunningActionId, setLocalRunningActionId] = useState<string | null>(null);

  useEffect(() => {
    if (isMobileOverride !== undefined) {
      return;
    }

    const onResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobileOverride]);

  useEffect(() => {
    if (!isEditingFilters) {
      setDraftControls(filters?.controls ?? []);
    }
  }, [filters?.controls, isEditingFilters]);

  useEffect(() => {
    setSearchInput(search?.value ?? '');
  }, [search?.value]);

  const searchDispatcher = useMemo(() => {
    if (!search) {
      return null;
    }

    return createDebouncedDispatcher(search.onSearch, search.debounceMs ?? 250);
  }, [search]);

  useEffect(() => {
    return () => {
      searchDispatcher?.cancel();
    };
  }, [searchDispatcher]);

  const effectiveIsMobile = isMobileOverride ?? isSmallScreen;

  const iconActions = actions?.iconActions ?? [];
  const allActions = iconActions;

  const sortedIconActions = useMemo(() => {
    return iconActions
      .map((action, index) => ({ action, index }))
      .sort((a, b) => {
        const groupDiff = (a.action.group ?? 0) - (b.action.group ?? 0);
        if (groupDiff !== 0) return groupDiff;
        const orderDiff = (a.action.order ?? 0) - (b.action.order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return a.index - b.index;
      })
      .map((item) => item.action);
  }, [iconActions]);

  const effectiveRunningActionId = actions?.runningActionId ?? localRunningActionId;
  const runningAction = allActions.find((action) => action.id === effectiveRunningActionId) ?? null;
  const pendingAction = allActions.find((action) => action.id === pendingActionId) ?? null;
  const activeSection: 'filters' | 'actions' | null = runningAction
    ? 'actions'
    : pendingAction
      ? 'actions'
      : isEditingFilters
        ? 'filters'
        : null;

  const filterSummary = filters?.summaryText ?? buildFilterSummary(filters?.controls ?? []);

  const getToggleChecked = (controls: PageHeaderFilterControl<TValue>[], controlId: string): boolean | null => {
    const control = controls.find((candidate) => candidate.id === controlId);
    if (!control || control.type !== 'toggle') {
      return null;
    }

    return control.checked;
  };

  const isControlDisabled = (control: PageHeaderFilterControl<TValue>): boolean => {
    if (control.type !== 'select') {
      return control.disabled ?? false;
    }

    if (control.disabledWhen) {
      const checked = getToggleChecked(draftControls, control.disabledWhen.controlId);
      if (checked === control.disabledWhen.checked) {
        return true;
      }
    }

    return control.disabled ?? false;
  };

  const handleToggleControlChange = (controlId: string, checked: boolean) => {
    setDraftControls((current) =>
      current.map((control) => {
        if (control.id !== controlId || control.type !== 'toggle') {
          return control;
        }

        return {
          ...control,
          checked,
        };
      })
    );
  };

  const handleSelectControlChange = (controlId: string, value: TValue | TValue[]) => {
    setDraftControls((current) =>
      current.map((control) => {
        if (control.id !== controlId || control.type !== 'select') {
          return control;
        }

        return {
          ...control,
          value,
        };
      })
    );
  };

  const handleApplyFilters = () => {
    filters?.onApply(draftControls);
    setIsEditingFilters(false);
  };

  const handleCancelFilters = () => {
    setDraftControls(filters?.controls ?? []);
    setIsEditingFilters(false);
    filters?.onCancel?.();
  };

  const runAction = async (actionId: string) => {
    const action = allActions.find((candidate) => candidate.id === actionId);
    if (!action || action.disabled) {
      return;
    }

    setPendingActionId(null);
    setLocalRunningActionId(action.id);

    try {
      await action.onExecute();
    } finally {
      setLocalRunningActionId((current) => (current === action.id ? null : current));
    }
  };

  const queueAction = (actionId: string) => {
    const action = allActions.find((candidate) => candidate.id === actionId);
    if (!action || action.disabled) {
      return;
    }

    if (action.requiresConfirm ?? true) {
      setPendingActionId(actionId);
      return;
    }

    void runAction(actionId);
  };

  const renderFiltersSection = () => {
    if (!filters) {
      return null;
    }

    return (
      <div style={{ ...sectionContainerStyle, flex: isEditingFilters ? '1 1 auto' : effectiveIsMobile ? '1 1 auto' : '0 0 auto' }}>
        {!isEditingFilters && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, width: '100%' }}>
            <IonText style={{ fontSize: '0.65rem', color: 'var(--seesor-dark-blue, #2d3035)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto', fontStyle: 'italic' }}>
              <span style={{ fontSize: '0.85rem', paddingRight: '4px', fontStyle: 'normal'  }}>Showing: </span> {filterSummary}
            </IonText>

            <ActionIconButton
              icon={createOutline}
              label={filters.changeLabel ?? 'change filters'}
              disabled={false}
              onClick={() => setIsEditingFilters(true)}
            />

          </div>
        )}

        {isEditingFilters && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', minWidth: 0, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 auto', minWidth: 0 }}>
              {draftControls.map((control) => {
                if (control.type === 'toggle') {
                  return (
                    <div
                      key={control.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        whiteSpace: 'nowrap',
                        flex: '0 0 auto',
                      }}
                    >
                      <IonText style={{ fontSize: '0.85rem', fontWeight: 500 }}>{control.label}</IonText>
                      <IonToggle
                        checked={control.checked}
                        disabled={control.disabled}
                        onIonChange={(event) => handleToggleControlChange(control.id, event.detail.checked)}
                        style={{ minHeight: '32px' }}
                      />
                    </div>
                  );
                }

                return (
                  <div key={control.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', flex: '0 1 auto', minWidth: 0 }}>
                    <IonText style={{ fontSize: '0.85rem', fontWeight: 500 }}>{control.label}</IonText>
                    <IonSelect
                      value={control.value}
                      multiple={control.multiple}
                      disabled={isControlDisabled(control)}
                      placeholder={control.placeholder}
                      selectedText={control.selectedText}
                      onIonChange={(event) => handleSelectControlChange(control.id, event.detail.value as TValue | TValue[])}
                      style={{
                        minWidth: effectiveIsMobile ? '132px' : '160px',
                        maxWidth: effectiveIsMobile ? '160px' : '220px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        background: '#ffffff',
                        color: 'var(--ion-color-dark)',
                        border: '1px solid #d6dfeb',
                        borderRadius: '999px',
                        minHeight: '36px',
                      }}
                    >
                      {control.options.map((option) => (
                        <IonSelectOption key={`${control.id}-${String(option.value)}`} value={option.value}>
                          {option.label}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
              <IonButton fill="outline" size="small" style={compactButtonStyle} onClick={handleCancelFilters}>
                {filters.cancelLabel ?? 'Cancel'}
              </IonButton>
              <IonButton size="small" style={compactButtonStyle} onClick={handleApplyFilters}>
                {filters.applyLabel ?? 'Apply'}
              </IonButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSearchSection = () => {
    if (!search) {
      return null;
    }

    return (
      <div style={{ ...sectionContainerStyle, flex: '1 1 220px', minWidth: effectiveIsMobile ? '120px' : '180px' }}>
        <IonInput
          value={searchInput}
          placeholder={search.placeholder}
          style={{
            minWidth: 0,
            width: '100%',
            paddingLeft: '10px',
            paddingRight: '10px',
            background: '#ffffff',
            color: 'var(--ion-color-dark)',
            minHeight: '36px',
            '--padding-start': '1.25rem',
            // border: '1px solid #d6dfeb',
            // borderRadius: '999px',
          }}
          onIonInput={(event) => {
            const nextValue = event.detail.value ?? '';
            setSearchInput(nextValue);
            searchDispatcher?.dispatch(nextValue);
          }}
        />
      </div>
    );
  };

  const renderActionConfirmation = () => {
    if (!pendingAction) {
      return null;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <IonText style={{ fontSize: '0.85rem' }}>Confirm {pendingAction.label}?</IonText>
        <IonButton
          size="small"
          color="danger"
          style={compactButtonStyle}
          onClick={() => {
            void runAction(pendingAction.id);
          }}
        >
          {pendingAction.confirmLabel ?? actions?.confirmLabel ?? 'Confirm'}
        </IonButton>
        <IonButton fill="outline" size="small" style={compactButtonStyle} onClick={() => setPendingActionId(null)}>
          {actions?.cancelLabel ?? 'Cancel'}
        </IonButton>
      </div>
    );
  };

  const renderRunningState = () => {
    if (!runningAction) {
      return null;
    }

    const runningProgress = actions?.runningActionProgress;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IonSpinner name="crescent" style={{ width: '16px', height: '16px' }} />
          <IonText style={{ fontSize: '0.85rem' }}>{runningAction.label}</IonText>
        </div>

        {runningProgress?.mode === 'determinate' && (
          <>
            <IonProgressBar value={Math.max(0, Math.min(100, runningProgress.value ?? 0)) / 100} />
            <IonText style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium-shade)' }}>
              {Math.max(0, Math.min(100, Math.round(runningProgress.value ?? 0)))}%
            </IonText>
          </>
        )}

        {runningProgress?.mode !== 'determinate' && <IonProgressBar type="indeterminate" />}
      </div>
    );
  };

  const renderActionsSection = () => {
    if (!actions) {
      return null;
    }

    return (
      <div style={{ ...sectionContainerStyle, flex: activeSection === 'actions' ? '1 1 auto' : '0 0 auto', justifyContent: 'flex-end' }}>

        {runningAction && renderRunningState()}

        {!runningAction && pendingAction && renderActionConfirmation()}

        {!runningAction && !pendingAction && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
            {sortedIconActions.map((action: PageHeaderIconAction, index) => {
              const currentGroup = action.group ?? 0;
              const previousGroup = index > 0 ? (sortedIconActions[index - 1].group ?? 0) : null;
              const showSeparator = previousGroup !== null && previousGroup !== currentGroup;

              return (
                <React.Fragment key={action.id}>
                  {showSeparator && (
                    <div style={{ width: '1px', height: '24px', alignSelf: 'center', background: 'var(--ion-color-light-shade)' }} />
                  )}
                  <ActionIconButton
                    icon={action.icon}
                    label={action.label}
                    disabled={action.disabled}
                    onClick={() => queueAction(action.id)}
                  />
                </React.Fragment>
              );
            })}

          </div>
        )}
      </div>
    );
  };

  const sectionComponents: Record<'filters' | 'search' | 'actions', React.ReactNode> = {
    filters: renderFiltersSection(),
    search: renderSearchSection(),
    actions: renderActionsSection(),
  };

  const visibleSections = sectionOrder.filter((sectionKey) => {
    if (activeSection === null) {
      return sectionComponents[sectionKey] != null;
    }

    return sectionKey === activeSection && sectionComponents[sectionKey] != null;
  });

  return (
    <div>
      {collapsible && (
        <button
          onClick={() => setIsCollapsed((current) => !current)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 2px 4px 0',
            color: 'var(--ion-color-primary)',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            // textTransform: 'uppercase',
            marginBottom: '6px',
          }}
        >
          <IonIcon
            icon={chevronDownOutline}
            style={{
              transition: 'transform 0.2s',
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              fontSize: '16px',
            }}
          />
          {isCollapsed ? 'Expand' : 'Collapse'} {collapseLabel}
        </button>
      )}

      {!isCollapsed && (
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              flexWrap: 'nowrap',
              minWidth: 0,
            }}
          >
            {visibleSections.map((sectionKey) => (
              <React.Fragment key={sectionKey}>{sectionComponents[sectionKey]}</React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeaderBar;
